// Firebase Phone OTP configuration.
//
// Fill these values from Firebase console → Project settings → Your apps:
//   REACT_APP_FIREBASE_API_KEY=
//   REACT_APP_FIREBASE_AUTH_DOMAIN=<project>.firebaseapp.com
//   REACT_APP_FIREBASE_PROJECT_ID=
//   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=
//   REACT_APP_FIREBASE_APP_ID=
// Then enable Phone sign-in in Authentication → Sign-in method,
// and add your server key (service account) to backend/.env for
// server-side token verification (FIREBASE_* variables).
import { initializeApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseEnabled = !!(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.appId);

let auth = null;
if (firebaseEnabled) {
  try {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
  } catch (e) {
    console.error('Firebase init failed:', e.message);
    auth = null;
  }
}

/**
 * Invisible reCAPTCHA management.
 * The container lives directly under <body> (outside React) so component
 * re-renders never orphan the widget ("Cannot read properties of null").
 */
let __verifier = null;

function ensureContainer() {
  let el = document.getElementById('lp-recaptcha-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'lp-recaptcha-root';
    el.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;';
    document.body.appendChild(el);
  }
  return el;
}

async function createRecaptcha() {
  if (!auth) return null;
  // Reset any previous widget first
  if (__verifier) {
    try { await __verifier.clear(); } catch { /* */ }
    __verifier = null;
  }
  const container = ensureContainer();
  const verifier = new RecaptchaVerifier(auth, container, { size: 'invisible' });
  __verifier = verifier;
  return verifier;
}

/** Send an SMS OTP to the given E.164 phone number (+937xxxxxxx). */
export async function sendPhoneOtp(phone /*, containerId ignored */) {
  const verifier = await createRecaptcha();
  if (!verifier) return null;
  try {
    return await signInWithPhoneNumber(auth, phone, verifier); // confirmationResult
  } catch (err) {
    // A failed attempt invalidates the widget — rebuild it for the next try
    try { await verifier.clear(); } catch { /* */ }
    __verifier = null;
    throw err;
  }
}

/** Confirm the 6-digit code. Resolves { phoneNumber, uid, idToken }. */
export async function confirmPhoneOtp(confirmationResult, code) {
  const cred = await confirmationResult.confirm(code);
  const idToken = await cred.user.getIdToken();
  return {
    phoneNumber: cred.user.phoneNumber,
    uid: cred.user.uid,
    idToken,
  };
}

/** Sign in with a Google popup. Resolves { email, name, uid, idToken }. */
export async function signInWithGoogle() {
  if (!auth) return null;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  const profile = result.user.providerData.find(p => p.providerId === 'google.com') || {};
  return {
    email: profile.email || result.user.email,
    name: profile.displayName || result.user.displayName || '',
    photoURL: profile.photoURL || result.user.photoURL || null,
    uid: result.user.uid,
    idToken,
  };
}
